// Package Block
import { useEffect, useState } from "react";
import { Row, Col } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import { shallowEqual, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

// Custom Components & Functions Block
import { PURCHASE_ORDER_STATUS, SALE_ORDER_RETURN_STATUS, ORDER_STATUS } from "../../../../Common/constants";
import { formatAndShortCurrency, getSettingKey, handleApiErrorResponse, navigateToInvoiceHistoryByOverduePeriod } from "../../../../Common/utils";
import { DashboardNotificatonSection } from "../BasicStats/notification/Notification";
import { ListStatsWidget } from "../../../../../../_metronic/_partials/widgets";
import { Card, Empty, Spin, Tooltip, Typography } from "antd";
import { AgingTable } from "../BasicStats/aging-table/AgingTable";
import { useDashboardBalanceVisibility } from "../balanceVisibility";
import { getAgingData } from "../../../_redux/dashboard/dashboardCrud";
const { Text } = Typography;

const AGING_SUMMARY_BUCKETS = [
  { key: "0_7_days", label: "0-7 Days", color: "#FDE8EE" },
  { key: "7_14_days", label: "7-14 Days", color: "#F9BBCB" },
  { key: "14_30_days", label: "14-30 Days", color: "#EE4B74" },
  { key: "30_60_days", label: "30-60 Days", color: "#EA1E51" },
  { key: "60_90_days", label: "60-90 Days", color: "#820014" },
  { key: "90_plus", label: "90+ Days", color: "#4A0718" },
];

const parseAgingSummary = (agingSummary) => {
  if (!agingSummary) {
    return {};
  }

  if (typeof agingSummary === "object") {
    return agingSummary;
  }

  try {
    return JSON.parse(agingSummary.replace(/'/g, '"'));
  } catch (err) {
    return {};
  }
};

const getAgingSummary = (agingInvoices) => {
  const summary = agingInvoices?.aging_summary || agingInvoices?.summary;

  if (summary) {
    return summary;
  }

  const entities = agingInvoices?.entities || [];

  if (!Array.isArray(entities)) {
    return {};
  }

  return entities.reduce((acc, entity) => {
    const entitySummary = parseAgingSummary(entity?.aging_summary);

    AGING_SUMMARY_BUCKETS.forEach((bucket) => {
      acc[bucket.key] = Number(acc[bucket.key] || 0) + Number(entitySummary?.[bucket.key] || 0);
    });

    return acc;
  }, {});
};

const AgingSummaryChart = ({ agingSummary, currencySymbol, onChartClick }) => {
  const { formatCurrencyValue } = useDashboardBalanceVisibility();
  const summary = parseAgingSummary(agingSummary);
  const chartData = AGING_SUMMARY_BUCKETS.map((bucket) => ({
    ...bucket,
    value: Number(summary?.[bucket.key] || 0),
  }));
  const total = chartData.reduce((sum, bucket) => sum + bucket.value, 0);
  const center = 150;
  const radius = 125;
  let currentAngle = -90;
  const formatAmount = (amount) => formatCurrencyValue(amount, currencySymbol);
  const getPoint = (angle) => {
    const angleInRadians = (Math.PI / 180) * angle;
    return {
      x: center + radius * Math.cos(angleInRadians),
      y: center + radius * Math.sin(angleInRadians),
    };
  };

  return (
    <Card className="mb-5" bodyStyle={{ padding: "18px 22px" }}>
      {total ? (
        <div style={{justifyItems:"center"}}>
          <div style={{ width: 300, height: 300, position: "relative" }} className="mr-8 mb-4">
            <svg width="300" height="300" viewBox="0 0 300 300" onClick={onChartClick} style={{ cursor: "pointer" }}>
              {chartData.map((bucket) => {
                if (!bucket.value) {
                  return null;
                }

                const startAngle = currentAngle;
                const endAngle = currentAngle + (bucket.value / total) * 360;
                const startPoint = getPoint(startAngle);
                const endPoint = getPoint(endAngle);
                const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;
                currentAngle = endAngle;

                const percent = total ? Math.round((bucket.value / total) * 100) : 0;

                if (bucket.value === total) {
                  return (
                    <Tooltip key={bucket.key} title={`${bucket.label}: ${formatAmount(bucket.value)} (${percent}%)`}>
                      <circle cx={center} cy={center} r={radius} fill={bucket.color} style={{ cursor: "pointer" }} />
                    </Tooltip>
                  );
                }

                return (
                  <Tooltip key={bucket.key} title={`${bucket.label}: ${formatAmount(bucket.value)} (${percent}%)`}>
                    <path
                      d={`M ${center} ${center} L ${startPoint.x} ${startPoint.y} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endPoint.x} ${endPoint.y} Z`}
                      fill={bucket.color}
                      style={{ cursor: "pointer" }}
                    />
                  </Tooltip>
                );
              })}
            </svg>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", justifyContent:"center" }}>
            {chartData.map((bucket) => {
              return (
                <div key={bucket.key} className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <span style={{ width: 12, height: 12, background: bucket.color, borderRadius: "50%", display: "inline-block", marginRight: 8 }} />
                    <Text>{bucket.label}</Text>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No aging summary available" />
      )}
    </Card>
  );
};

export const AgingDashboardSection = () => {
  const { currentState } = useSelector((state) => ({ currentState: state.basicSettings }), shallowEqual);
  const currencySymbol = getSettingKey(currentState.entity, "Basic", "currency_symbol");
  const history = useHistory();
  const [agingInvoices, setAgingInvoices] = useState([]);
  const [queryParams, setQueryParams] = useState({ page: 1, page_size: 20, sort_by: "created_at:asc" });
  const [agingLoading, setAgingLoading] = useState(false);
  const agingSummary = getAgingSummary(agingInvoices);

  const getAgingInvoiceData = async () => {
    try {
      setAgingLoading(true);
      const response = await getAgingData({ page_size: 20, page: 1, ...queryParams });
      setAgingInvoices(response.data);
    } catch (err) {
      handleApiErrorResponse(err);
    } finally {
      setAgingLoading(false);
    }
  };

  useEffect(() => {
    getAgingInvoiceData();
  }, [queryParams]);

  return (
    <Spin spinning={agingLoading}>
      <Row style={{ marginBottom: "30px" }}>
        <Col lg={8} xxl={8}>
          <AgingTable agingInvoices={agingInvoices} queryParams={queryParams} setQueryParams={setQueryParams} listLoading={agingLoading} />
        </Col>
        <Col lg={4} xxl={4}>
          <AgingSummaryChart
            agingSummary={agingSummary}
            currencySymbol={currencySymbol}
            onChartClick={() => navigateToInvoiceHistoryByOverduePeriod(history)}
          />
        </Col>
      </Row>
    </Spin>
  );
};

const SectionTwo = ({ purchase_orders, purchase_receives, purchase_returns, sale_orders, sales_return, loading, startDate, endDate }) => {
  const { currentState } = useSelector((state) => ({ currentState: state.basicSettings }), shallowEqual);
  const currencySymbol = getSettingKey(currentState.entity, "Basic", "currency_symbol");
  const { formatCurrencyValue } = useDashboardBalanceVisibility();

  return (
    <Spin spinning={loading}>
      <Row>
        <Col lg={4} xxl={4}>
          <ListStatsWidget
            name="Sale Order Status"
            constant={ORDER_STATUS}
            section="sale"
            data={sale_orders}
            className="card-stretch gutter-b"
            currency={currencySymbol}
            formatCurrencyValue={(amount) => formatCurrencyValue(amount, currencySymbol, (value) => formatAndShortCurrency(value).toUpperCase())}
          />
        </Col>
        <Col lg={4} xxl={4}>
          <ListStatsWidget
            name={<FormattedMessage id="Purchase Order(s)" defaultMessage="Purchase Order(s)" />}
            constant={PURCHASE_ORDER_STATUS}
            data={purchase_orders}
            className="card-stretch gutter-b"
            currency={currencySymbol}
            formatCurrencyValue={(amount) => formatCurrencyValue(amount, currencySymbol, (value) => formatAndShortCurrency(value).toUpperCase())}
          />
        </Col>
        <Col lg={4} xxl={4}>
          <ListStatsWidget
            name={<FormattedMessage id="Return Order(s)" defaultMessage="Return Order(s)" />}
            constant={SALE_ORDER_RETURN_STATUS}
            data={sales_return}
            className="card-stretch gutter-b"
            currency={currencySymbol}
            formatCurrencyValue={(amount) => formatCurrencyValue(amount, currencySymbol, (value) => formatAndShortCurrency(value).toUpperCase())}
          />
        </Col>
      </Row>
      <DashboardNotificatonSection startDate={startDate} endDate={endDate}/>    </Spin>
  );
};

export default SectionTwo;
